import React, { forwardRef, ReactElement, SyntheticEvent } from 'react';
import { styled, Tab, TabProps, Tabs, TabsProps, Tooltip } from '@mui/material';
import { interpolateCSSVariable } from 'new/theme';

export type ControlProps = {
  value: any;
  icon?: ReactElement;
  disabled?: boolean;
  label: string;
};

type SegmentedControlProps = {
  controls: ControlProps[];
  iconOnly?: boolean;
  iconPosition?: 'start' | 'end' | 'top' | 'bottom';
  defaultValue: any;
  value: any;
  onControlChange: (event: SyntheticEvent, value: any) => void;
} & TabsProps;

const SegmentTabs = styled(Tabs)(() => ({
  minHeight: '0px',
  width: 'fit-content',
  '& .MuiTabs-indicator': {
    visibility: 'hidden',
  },
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
    '& :first-of-type': {
      borderTopLeftRadius: '4px',
      borderBottomLeftRadius: '4px',
    },
    '& :last-child': {
      borderTopRightRadius: '4px',
      borderBottomRightRadius: '4px',
    },
  },
}));

const StyledSegment = styled(Tab, {
  shouldForwardProp: (prop) => prop !== 'iconOnly',
  // @ts-ignore
})(({ theme, label, iconOnly }) => ({
  fontSize: 12,
  color: theme.palette.neutral.grey600,
  border: '1px solid ' + interpolateCSSVariable('--color-primary-200'),
  borderLeft: 'none',
  borderRight: 'none',
  svg: {
    width: '16px',
    height: '16px',
    fill: theme.palette.neutral.grey600,
    color: theme.palette.neutral.grey600,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    svg: {
      fill: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    border: '1px solid ' + interpolateCSSVariable('--color-primary-200'),
    '&:before, + button:before': {
      opacity: 0,
    },
  },
  '&:first-of-type': {
    borderLeft: '1px solid ' + interpolateCSSVariable('--color-primary-200'),
  },
  '&:last-child': {
    borderRight: '1px solid ' + interpolateCSSVariable('--color-primary-200'),
  },
  '&:not(:first-of-type)::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: '1px',
    background: interpolateCSSVariable('--color-primary-200'),
  },
  minWidth: !!label ? undefined : '32px',
  minHeight: iconOnly ? '32px' : '30px',
  paddingTop: '4px',
  paddingBottom: '4px',
}));

const Segment = forwardRef<HTMLDivElement, TabProps & { iconOnly: boolean }>((params, ref) => {
  const { children, ...props } = params;
  return <StyledSegment aria-label="segment" ref={ref} {...props} />;
});

export function SegmentedControl(props: SegmentedControlProps) {
  const { onControlChange, iconOnly, iconPosition, controls, ...rest } = props;
  const handleChange = (event, newValue) => {
    if (onControlChange) {
      onControlChange(event, newValue);
    }
  };

  return (
    <SegmentTabs {...rest} onChange={handleChange}>
      {controls.map((tab) => {
        return (
          <Segment
            iconOnly={iconOnly}
            key={tab.label}
            disabled={tab.disabled}
            value={tab.value}
            iconPosition={iconPosition}
            icon={iconOnly ? <Tooltip title={tab.label}>{tab.icon}</Tooltip> : tab.icon}
            label={iconOnly ? undefined : tab.label}
          />
        );
      })}
    </SegmentTabs>
  );
}
